import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import { doc, updateDoc, arrayUnion, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';
import Comment from './Comment';

const CommentSection = ({ postId }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [commentText, setCommentText] = useState('');

  useEffect(() => {
    setLoading(true);
    const postRef = doc(db, 'posts', postId);
    
    // Real-time listener for comments
    const unsubscribe = onSnapshot(postRef, (docSnap) => {
      if (docSnap.exists()) {
        const postData = docSnap.data();
        const commentsData = postData.comments || [];
        // Ensure comments is always an array
        setComments(Array.isArray(commentsData) ? commentsData : []);
      }
      setLoading(false);
    }, (error) => {
      if (error.code === 'permission-denied') {
        console.warn('Comments disabled: Firebase permissions not configured');
        toast.error('Comments temporarily unavailable');
      } else {
        console.error('Error fetching comments:', error);
      }
      setComments([]);
      setLoading(false);
    });

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [postId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim() || !commentText.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const newComment = {
        id: `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: name.trim(),
        text: commentText.trim(),
        createdAt: new Date(),
        likes: 0,
        parentId: null,
      };

      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        comments: arrayUnion(newComment),
        commentCount: comments.length + 1,
      });

      toast.success('Comment posted successfully!');
      setName('');
      setCommentText('');
      // Real-time listener will automatically update comments
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  // Separate parent comments and replies
  const parentComments = Array.isArray(comments) ? comments.filter((c) => !c.parentId) : [];
  const getReplies = (commentId) => {
    return Array.isArray(comments) ? comments.filter((c) => c.parentId === commentId) : [];
  };

  return (
    <div className="mt-16 pt-12 border-t border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <MessageCircle size={28} className="mr-3" />
          <h2 className="heading-3">
            Comments ({Array.isArray(comments) ? comments.length : 0})
          </h2>
        </div>
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="font-body">Live updates</span>
        </div>
      </div>

      {/* Comment Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-12"
      >
        <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
          <h3 className="font-body font-semibold text-lg mb-4">Leave a Comment</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-body font-medium mb-2">
                Your Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-body font-medium mb-2">
                Your Comment *
              </label>
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Share your thoughts..."
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black resize-none"
                required
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-3 bg-black text-white rounded-lg font-body font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Posting...' : 'Post Comment'}
            </button>
          </form>
        </div>
      </motion.div>

      {/* Comments List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-black"></div>
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 font-body">
            No comments yet. Be the first to share your thoughts!
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {parentComments.map((comment) => (
            <div key={comment.id}>
              <Comment 
                comment={comment} 
                postId={postId}
              />
              {/* Render replies */}
              {getReplies(comment.id).map((reply) => (
                <Comment
                  key={reply.id}
                  comment={reply}
                  postId={postId}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentSection;
